import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useUploadQueue } from "../contexts/UploadQueueContext";
import { isMsalConfigured } from "../config/msalInstance";
import { takeMicrosoftLoginError } from "../config/microsoftLogin";
import { MedewerkerType as Role } from "../enums";
import { neemDoel } from "../utils/naLoginDoel";
import logo from "../assets/expertisepunt-logo.png";
import styles from "./LoginPage.module.css";

// Technische/Engelse foutteksten uit de fetch-laag of de backend hier in het
// Nederlands tonen; onbekende meldingen laten we ongewijzigd staan.
const NL_FOUTTEKST: Record<string, string> = {
  "Failed to fetch": "Geen verbinding met de server. Probeer het zo opnieuw.",
  "Login failed": "Inloggen is niet gelukt, controleer je gegevens.",
  Unauthorized: "Inloggen is niet gelukt, controleer je gegevens.",
};

const foutTekst = (err: unknown) => {
  const melding = err instanceof Error ? err.message : "";
  return (
    NL_FOUTTEKST[melding] ||
    melding ||
    "Inloggen is niet gelukt. Probeer het opnieuw."
  );
};

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showDevLogin, setShowDevLogin] = useState(false);
  const { login, loginWithMicrosoft, msalReady } = useAuth();
  // Sessie verlopen tijdens een upload: inloggen in dít tabblad herlaadt de
  // pagina (Microsoft-redirect) en dan is de wachtrij weg. Daarom in een nieuw
  // tabblad; dit tabblad ziet de nieuwe sessie vanzelf en gaat verder.
  const { gepauzeerd } = useUploadQueue();
  const navigate = useNavigate();
  const msalConfigured = isMsalConfigured();
  // Lokale dev-login staat standaard UIT: alleen tonen als die expliciet is
  // ingeschakeld (REACT_APP_ENABLE_DEV_LOGIN="true"). In dev/productie loggen
  // medewerkers uitsluitend via Microsoft SSO in. Fallback: als MSAL niet is
  // geconfigureerd blijft lokale login bereikbaar om jezelf niet buiten te sluiten.
  const enableDevLogin =
    !msalConfigured || process.env.REACT_APP_ENABLE_DEV_LOGIN === "true";

  // Fout uit een mislukte Microsoft-redirect (gezet vóór de render in
  // completeMicrosoftRedirect) eenmalig ophalen en tonen.
  useEffect(() => {
    const stored = takeMicrosoftLoginError();
    if (stored) setError(stored);
  }, []);

  const getRedirectPath = (roles?: Role[]) =>
    roles?.some((r) => [Role.PM, Role.MANAGER, Role.ADMIN].includes(r))
      ? "/manager-dashboard"
      : "/dashboard";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const user = await login(email, password);
      // Bewaard doel (bijv. een link uit een WhatsApp-melding) gaat voor.
      navigate(neemDoel() ?? getRedirectPath(user?.roles));
    } catch (err: any) {
      setError(foutTekst(err));
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    if (!msalReady) {
      setError("Inloggen wordt nog voorbereid. Een moment geduld…");
      return;
    }

    setLoading(true);
    setError("");
    try {
      // Redirect-flow: de browser verlaat de app richting Microsoft; na
      // terugkeer stuurt de router de ingelogde gebruiker zelf door.
      await loginWithMicrosoft();
    } catch (err: any) {
      setError(foutTekst(err));
      setLoading(false);
    }
  };

  // Knoptekst blijft bewust neutraal: geen leveranciersnaam op de knop.
  const knopTekst = loading
    ? "Bezig met inloggen…"
    : msalReady
      ? "Inloggen"
      : "Even geduld…";

  return (
    <div className={styles.page}>
      <div className={styles.punt} aria-hidden="true" />

      <div className={styles.card}>
        <img src={logo} alt="Expertisepunt" className={styles.logo} />
        <h1 className={styles.titel}>Welkom terug</h1>
        <p className={styles.sub}>Log in om verder te gaan met je dossiers.</p>

        {/* Foutmelding altijd zichtbaar — óók als alleen de SSO-knop wordt getoond */}
        {error && (
          <p role="alert" className={styles.fout}>
            {error}
          </p>
        )}

        {gepauzeerd && (
          <p role="status" className={styles.melding}>
            Je sessie is verlopen terwijl er een upload liep. De upload staat
            stil en gaat vanzelf verder zodra je weer bent ingelogd. Log in via
            een nieuw tabblad en laat dit tabblad open.
          </p>
        )}

        {msalConfigured && (
          <>
            <button
              type="button"
              onClick={
                gepauzeerd
                  ? () => window.open(window.location.origin, "_blank")
                  : handleMicrosoftLogin
              }
              disabled={!msalReady || loading}
              className={styles.knop}
            >
              {gepauzeerd ? "Inloggen in een nieuw tabblad" : knopTekst}
            </button>

            {enableDevLogin && (
              <div>
                <button
                  type="button"
                  onClick={() => setShowDevLogin(!showDevLogin)}
                  className={styles.devToggle}
                >
                  {showDevLogin ? "Verbergen" : "Ontwikkelaarslogin"}
                </button>
              </div>
            )}
          </>
        )}

        {(!msalConfigured || showDevLogin) && (
          <form
            onSubmit={handleSubmit}
            className={msalConfigured ? styles.devForm : undefined}
          >
            <label htmlFor="email" className={styles.label}>
              E-mailadres
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              className={styles.input}
            />

            <label htmlFor="password" className={styles.label}>
              Wachtwoord
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              className={styles.input}
            />

            <button type="submit" disabled={loading} className={styles.knop}>
              {loading
                ? "Bezig met inloggen…"
                : msalConfigured
                  ? "Inloggen met wachtwoord"
                  : "Inloggen"}
            </button>
          </form>
        )}
      </div>

      <p className={styles.hulp}>
        Lukt inloggen niet? Neem contact op met Niels.
      </p>
    </div>
  );
};

export default Login;
